
import { useState, useMemo, useEffect } from 'react';
import GuidelineCard from '../../../components/base/GuidelineCard';
import FilterTabs from '../../../components/base/FilterTabs';
import { mockGuidelines } from '../../../mocks/guidelines';

interface GuidelinesGridProps {
  searchQuery: string;
  selectedAgency: string;
  selectedCategory: string;
}

export default function GuidelinesGrid({ searchQuery, selectedAgency, selectedCategory }: GuidelinesGridProps) {
  const [sortBy, setSortBy] = useState('relevance');
  const [activeFilter, setActiveFilter] = useState('');

  // Update active filter when selectedAgency changes
  useEffect(() => {
    if (selectedAgency) {
      setActiveFilter(selectedAgency);
    }
  }, [selectedAgency]);

  const filteredGuidelines = useMemo(() => {
    let filtered = mockGuidelines;

    // Filter by search query
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(guideline =>
        guideline.title.toLowerCase().includes(searchLower) ||
        guideline.description.toLowerCase().includes(searchLower) ||
        guideline.source.toLowerCase().includes(searchLower) ||
        guideline.category.toLowerCase().includes(searchLower) ||
        guideline.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    // Filter by agency (from filter tabs or parent component)
    const agencyFilter = activeFilter || selectedAgency;
    if (agencyFilter && agencyFilter !== 'all' && agencyFilter !== '') {
      filtered = filtered.filter(guideline => guideline.source === agencyFilter);
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(guideline => guideline.category === selectedCategory);
    }

    // Sort guidelines
    if (sortBy === 'date') {
      filtered = [...filtered].sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
    } else if (sortBy === 'agency') {
      filtered = [...filtered].sort((a, b) => a.source.localeCompare(b.source));
    } else if (sortBy === 'relevance' && searchQuery.trim()) {
      // Simple relevance scoring based on title matches
      filtered = [...filtered].sort((a, b) => {
        const aScore = a.title.toLowerCase().includes(searchQuery.toLowerCase()) ? 2 : 1;
        const bScore = b.title.toLowerCase().includes(searchQuery.toLowerCase()) ? 2 : 1;
        return bScore - aScore;
      });
    }

    return filtered;
  }, [searchQuery, selectedAgency, selectedCategory, sortBy, activeFilter]);

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
  };

  const clearAllFilters = () => {
    setActiveFilter('');
    // This will trigger a re-render with no filters
  };

  const getFilterDisplayText = () => {
    const filters = [];
    if (activeFilter && activeFilter !== 'all') {
      filters.push(`Agency: ${activeFilter}`);
    }
    if (selectedCategory) {
      filters.push(`Category: ${selectedCategory}`);
    }
    return filters.length > 0 ? ` • ${filters.join(' • ')}` : '';
  };

  return (
    <section className="bg-slate-900 py-16" id="guidelines">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">
              {searchQuery.trim() ? `Search Results for "${searchQuery}"` : 'All Guidelines'}
            </h2>
            <p className="text-slate-400">
              {filteredGuidelines.length} guidelines found{getFilterDisplayText()}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <label className="text-slate-400 text-sm">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-slate-800 text-slate-300 border border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 pr-8"
            >
              <option value="relevance">Relevance</option>
              <option value="date">Last Updated</option>
              <option value="agency">Agency</option>
            </select>
          </div>
        </div>

        <FilterTabs 
          activeFilter={activeFilter || 'all'}
          onFilterChange={handleFilterChange}
        />

        {filteredGuidelines.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-slate-800 rounded-full">
              <i className="ri-search-line text-3xl text-slate-500"></i>
            </div>
            <h3 className="text-xl font-semibold text-slate-300 mb-2">No guidelines found</h3>
            <p className="text-slate-500 mb-6">
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
            <button 
              onClick={clearAllFilters}
              className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-500 font-medium cursor-pointer whitespace-nowrap transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGuidelines.map((guideline) => (
              <GuidelineCard key={guideline.id} guideline={guideline} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
